import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Calendar, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

export type DatePrecision = 'day' | 'month' | 'year' | 'year-range' | 'decade' | 'circa';
export type DateType = 'single' | 'range' | 'circa' | 'decade';

interface EnhancedDateInputProps {
  value?: string;
  precision?: DatePrecision;
  onValueChange?: (value: string, precision: DatePrecision) => void;
  placeholder?: string;
  className?: string;
}

const months = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

// Generate years from 1800 to current year
const currentYear = new Date().getFullYear();
const years = Array.from({ length: currentYear - 1799 }, (_, i) => currentYear - i).map(year => ({
  value: year.toString(),
  label: year.toString(),
}));

export function EnhancedDateInput({ 
  value = '', 
  precision = 'day', 
  onValueChange, 
  placeholder = 'Select date',
  className 
}: EnhancedDateInputProps) {
  const [selectedPrecision, setSelectedPrecision] = React.useState<DatePrecision>(precision);
  const [selectedYear, setSelectedYear] = React.useState<string>('');
  const [selectedMonth, setSelectedMonth] = React.useState<string>('');
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>();
  const [startYear, setStartYear] = React.useState<string>('');
  const [endYear, setEndYear] = React.useState<string>('');
  const [selectedDecade, setSelectedDecade] = React.useState<string>('');
  const [circaYear, setCircaYear] = React.useState<string>('');

  // Update selectedPrecision when precision prop changes
  React.useEffect(() => {
    setSelectedPrecision(precision);
  }, [precision]);

  // Parse existing value on mount
  React.useEffect(() => {
    if (value) {
      // Handle year range format like "1950-1960"
      if (selectedPrecision === 'year-range' && /^\d{4}-\d{4}$/.test(value)) {
        const [start, end] = value.split('-');
        setStartYear(start);
        setEndYear(end);
      }
      // Handle decade format like "1950s"
      else if (selectedPrecision === 'decade' && /^\d{4}s$/.test(value)) {
        setSelectedDecade(value);
      }
      // Handle circa format like "circa 1955"
      else if (selectedPrecision === 'circa' && value.startsWith('circa ')) {
        const year = value.replace('circa ', '');
        setCircaYear(year);
      }
      // Handle regular date formats
      else {
        const parts = value.split('-');
        if (parts.length >= 1) {
          setSelectedYear(parts[0]);
        }
        if (parts.length >= 2) {
          setSelectedMonth(parts[1]);
        }
        if (parts.length === 3) {
          setSelectedDate(new Date(value));
        }
      }
    }
  }, [value, selectedPrecision]);

  const handlePrecisionChange = (newPrecision: DatePrecision) => {
    setSelectedPrecision(newPrecision);
    
    // Reset values when precision changes
    if (newPrecision === 'year') {
      if (selectedYear) {
        const newValue = `${selectedYear}-01-01`;
        onValueChange?.(newValue, newPrecision);
      }
    } else if (newPrecision === 'month') {
      if (selectedYear && selectedMonth) {
        const newValue = `${selectedYear}-${selectedMonth}-01`;
        onValueChange?.(newValue, newPrecision);
      }
    }
  };

  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    
    if (selectedPrecision === 'year') {
      const newValue = `${year}-01-01`;
      onValueChange?.(newValue, selectedPrecision);
    } else if (selectedPrecision === 'month' && selectedMonth) {
      const newValue = `${year}-${selectedMonth}-01`;
      onValueChange?.(newValue, selectedPrecision);
    }
  };

  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    
    if (selectedPrecision === 'month' && selectedYear) {
      const newValue = `${selectedYear}-${month}-01`;
      onValueChange?.(newValue, selectedPrecision);
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    
    if (date && selectedPrecision === 'day') {
      const newValue = format(date, 'yyyy-MM-dd');
      onValueChange?.(newValue, selectedPrecision);
      
      // Update year and month from selected date
      setSelectedYear(format(date, 'yyyy'));
      setSelectedMonth(format(date, 'MM'));
    }
  };

  const getDisplayValue = () => {
    if (!value) return placeholder;
    
    // Handle different precision types
    switch (selectedPrecision) {
      case 'year':
        const date = new Date(value);
        const isValidDate = !isNaN(date.getTime());
        return selectedYear || (isValidDate ? format(date, 'yyyy') : value.split('-')[0] || placeholder);
      
      case 'month':
        if (selectedYear && selectedMonth) {
          const monthLabel = months.find(m => m.value === selectedMonth)?.label;
          return `${monthLabel} ${selectedYear}`;
        }
        const monthDate = new Date(value);
        return !isNaN(monthDate.getTime()) ? format(monthDate, 'MMMM yyyy') : value;
      
      case 'day':
        const dayDate = new Date(value);
        return !isNaN(dayDate.getTime()) ? format(dayDate, 'PPP') : value;
      
      case 'year-range':
        if (startYear && endYear) {
          return `${startYear}-${endYear}`;
        }
        return value; // Display as stored for ranges like "1950-1960"
      
      case 'decade':
        return selectedDecade || value; // Display as stored for decades like "1950s"
      
      case 'circa':
        return circaYear ? `circa ${circaYear}` : value; // Display as stored for circa dates
      
      default:
        return value || placeholder;
    }
  };

  return (
    <div className={cn('space-y-3', className)}>
      {/* Date Precision Selector */}
      <div>
        <Label className="text-sm font-medium">Date Precision</Label>
        <Select value={selectedPrecision} onValueChange={handlePrecisionChange}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Full Date (Day, Month, Year)</SelectItem>
            <SelectItem value="month">Month and Year</SelectItem>
            <SelectItem value="year">Year Only</SelectItem>
            <SelectItem value="year-range">Year Range (e.g., 1950-1960)</SelectItem>
            <SelectItem value="decade">Decade (e.g., 1950s)</SelectItem>
            <SelectItem value="circa">Circa Year (e.g., circa 1955)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Date Input Based on Precision */}
      <div>
        <Label className="text-sm font-medium">Date</Label>
        
        {selectedPrecision === 'year' && (
          <Select value={selectedYear} onValueChange={handleYearChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select year" />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {years.map((year) => (
                <SelectItem key={year.value} value={year.value}>
                  {year.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {selectedPrecision === 'month' && (
          <div className="grid grid-cols-2 gap-2">
            <Select value={selectedMonth} onValueChange={handleMonthChange}>
              <SelectTrigger>
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                {months.map((month) => (
                  <SelectItem key={month.value} value={month.value}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedYear} onValueChange={handleYearChange}>
              <SelectTrigger>
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {years.map((year) => (
                  <SelectItem key={year.value} value={year.value}>
                    {year.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {selectedPrecision === 'day' && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-full justify-start text-left font-normal',
                  !selectedDate && 'text-muted-foreground'
                )}
              >
                <Calendar className="mr-2 h-4 w-4" />
                {getDisplayValue()}
                <ChevronDown className="ml-auto h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <CalendarComponent
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                fromYear={1800}
                toYear={currentYear}
                captionLayout="dropdown"
                initialFocus
              />
            </PopoverContent>
          </Popover>
        )}

        {selectedPrecision === 'year-range' && (
          <div className="grid grid-cols-2 gap-2">
            <Select value={startYear} onValueChange={(year) => {
              setStartYear(year);
              if (year && endYear) {
                onValueChange?.(`${year}-${endYear}`, selectedPrecision);
              }
            }}>
              <SelectTrigger>
                <SelectValue placeholder="Start Year" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {years.map((year) => (
                  <SelectItem key={year.value} value={year.value}>
                    {year.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={endYear} onValueChange={(year) => {
              setEndYear(year);
              if (startYear && year) {
                onValueChange?.(`${startYear}-${year}`, selectedPrecision);
              }
            }}>
              <SelectTrigger>
                <SelectValue placeholder="End Year" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {years.map((year) => (
                  <SelectItem key={year.value} value={year.value}>
                    {year.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {selectedPrecision === 'decade' && (
          <Select value={selectedDecade} onValueChange={(decade) => {
            setSelectedDecade(decade);
            onValueChange?.(decade, selectedPrecision);
          }}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select decade" />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {Array.from({ length: Math.floor((currentYear - 1800) / 10) + 1 }, (_, i) => {
                const decade = 1800 + (i * 10);
                return (
                  <SelectItem key={decade} value={`${decade}s`}>
                    {decade}s
                  </SelectItem>
                );
              }).reverse()}
            </SelectContent>
          </Select>
        )}

        {selectedPrecision === 'circa' && (
          <Select value={circaYear} onValueChange={(year) => {
            setCircaYear(year);
            onValueChange?.(`circa ${year}`, selectedPrecision);
          }}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select circa year" />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {years.map((year) => (
                <SelectItem key={year.value} value={year.value}>
                  circa {year.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Display Current Value */}
      {value && (
        <div className="text-sm text-muted-foreground">
          <strong>Selected:</strong> {getDisplayValue()}
          {selectedPrecision !== 'day' && (
            <span className="ml-2 text-xs">
              (Stored as: {value})
            </span>
          )}
        </div>
      )}
    </div>
  );
}
